import { motion } from "framer-motion";
import { BellRing, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PhoneMockup } from "./PhoneMockup";

const S = "/assets/screens";

export const notifyEarlyAccess = () =>
  toast.success("You're on the list!", {
    description: "We'll notify you at launch. Every card matters.",
  });

export const Hero = () => {
  return (
    <section className="relative overflow-hidden pb-16 pt-28 sm:pb-20 sm:pt-32" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-toto-lavender blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-64 h-[26rem] w-[26rem] rounded-full bg-toto-violet-soft blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-toto-blue/15 bg-toto-lavender px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-toto-blue" data-testid="hero-badge">
            Coming soon
          </span>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            Turn Every Card Into a{" "}
            <span className="text-toto-blue">Connection.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-subtext">
            Scan visiting cards, organize your network, set follow-ups and build
            meaningful professional connections — all in one smart app.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={notifyEarlyAccess}
              data-testid="hero-get-early-access-button"
              className="inline-flex items-center gap-2 rounded-full bg-toto-blue px-7 py-3.5 text-base font-semibold text-white shadow-[0_12px_30px_rgba(27,44,200,0.3)] transition-all hover:-translate-y-0.5 hover:bg-toto-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-toto-blue focus-visible:ring-offset-2"
            >
              Get Early Access <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="#features"
              data-testid="hero-explore-features-button"
              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-7 py-3.5 text-base font-semibold text-slate-800 transition-all hover:-translate-y-0.5 hover:border-toto-blue/40 hover:text-toto-blue focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-toto-blue focus-visible:ring-offset-2"
            >
              Explore Features
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={notifyEarlyAccess}
              data-testid="hero-app-store-button"
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              aria-label="Download on the App Store (coming soon)"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 fill-slate-900" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08ZM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25Z"/></svg>
              <span><span className="block text-[10px] uppercase tracking-wide text-slate-500">Coming soon on</span><span className="block text-sm font-semibold text-slate-900">App Store</span></span>
            </button>
            <button
              onClick={notifyEarlyAccess}
              data-testid="hero-google-play-button"
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
              aria-label="Get it on Google Play (coming soon)"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true"><path fill="#1B2CC8" d="M3.6 2.3 13.7 12 3.6 21.7c-.37-.2-.6-.6-.6-1.1V3.4c0-.5.23-.9.6-1.1Z"/><path fill="#7A42F4" d="m16.9 8.8-3.2 3.2L4.4 2.7c.3-.16.68-.2 1.05-.02l11.45 6.12Z"/><path fill="#0F172A" d="m16.9 15.2-11.45 6.1c-.37.2-.75.15-1.05-.01l9.3-9.3 3.2 3.21Z"/></svg>
              <span><span className="block text-[10px] uppercase tracking-wide text-slate-500">Coming soon on</span><span className="block text-sm font-semibold text-slate-900">Google Play</span></span>
            </button>
          </div>
        </motion.div>

        <motion.div
          className="relative mx-auto w-full max-w-sm"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <PhoneMockup
            src={`${S}/home-dashboard.jpeg`}
            alt="TOTO home dashboard showing scan a card, goals and events"
            testId="hero-phone-mockup"
          />
          <motion.div
            className="absolute -left-10 top-16 hidden w-44 sm:block"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl" data-testid="hero-floating-goals-card">
              <img src={`${S}/goals.jpeg`} alt="TOTO networking goals screen" className="h-36 w-full object-cover object-top" loading="lazy" />
              <div className="flex items-center gap-2 px-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-toto-blue" />
                <span className="text-xs font-semibold text-slate-800">Goal completed</span>
              </div>
            </div>
          </motion.div>
          <motion.div
            className="absolute -right-8 bottom-20 hidden w-44 sm:block"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
          >
            <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl" data-testid="hero-floating-reminder-card">
              <img src={`${S}/notifications.jpeg`} alt="TOTO notifications screen" className="h-36 w-full object-cover object-top" loading="lazy" />
              <div className="flex items-center gap-2 px-3 py-2.5">
                <BellRing className="h-4 w-4 text-toto-purple" />
                <span className="text-xs font-semibold text-slate-800">Follow-up reminder</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
